import * as React from 'react';
import { Dimensions, Modal, Pressable, View } from 'react-native';
import Animated, {
  useAnimatedProps,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import Svg, { Defs, Mask, Rect } from 'react-native-svg';

const AnimatedRect = Animated.createAnimatedComponent(Rect);

interface TargetLayout {
  x: number;
  y: number;
  width: number;
  height: number;
}

type TooltipPlacement = 'above' | 'below';

interface SpotlightProps {
  visible: boolean;
  targetLayout: TargetLayout | null;
  onDismiss?: () => void;
  children?: React.ReactNode;
  cutoutPadding?: number;
  cutoutBorderRadius?: number;
  tooltipPosition?: 'auto' | TooltipPlacement;
  testID?: string;
}

const TOOLTIP_MAX_WIDTH = 320;
const SCREEN_MARGIN = 16;
const GAP = 12;
const ANIMATION_DURATION = 300;
const FADE_DURATION = 200;

function computePlacement(
  target: TargetLayout,
  padding: number,
  screenHeight: number,
  tooltipHeight: number,
  preference: 'auto' | TooltipPlacement,
): TooltipPlacement {
  if (preference !== 'auto') return preference;

  const cutoutBottom = target.y + target.height + padding;
  const cutoutTop = target.y - padding;
  const spaceBelow = screenHeight - cutoutBottom - GAP;
  const spaceAbove = cutoutTop - GAP;

  return spaceBelow >= tooltipHeight + SCREEN_MARGIN ? 'below' : 'above';
}

function computeTooltipTop(
  target: TargetLayout,
  padding: number,
  placement: TooltipPlacement,
  tooltipHeight: number,
): number {
  const cutoutBottom = target.y + target.height + padding;
  const cutoutTop = target.y - padding;

  return placement === 'below'
    ? cutoutBottom + GAP
    : cutoutTop - GAP - tooltipHeight;
}

function computeTooltipLeft(
  target: TargetLayout,
  screenWidth: number,
): number {
  const targetCenterX = target.x + target.width / 2;
  return Math.max(
    SCREEN_MARGIN,
    Math.min(
      targetCenterX - TOOLTIP_MAX_WIDTH / 2,
      screenWidth - TOOLTIP_MAX_WIDTH - SCREEN_MARGIN,
    ),
  );
}

function Spotlight({
  visible,
  targetLayout,
  onDismiss,
  children,
  cutoutPadding = 8,
  cutoutBorderRadius = 12,
  tooltipPosition = 'auto',
  testID,
}: SpotlightProps) {
  const { width: screenWidth, height: screenHeight } = Dimensions.get('window');
  const [tooltipHeight, setTooltipHeight] = React.useState(180);

  const overlayOpacity = useSharedValue(0);
  const cutoutX = useSharedValue(0);
  const cutoutY = useSharedValue(0);
  const cutoutW = useSharedValue(0);
  const cutoutH = useSharedValue(0);

  const prevLayoutRef = React.useRef<TargetLayout | null>(null);

  React.useEffect(() => {
    if (visible && targetLayout) {
      const x = targetLayout.x - cutoutPadding;
      const y = targetLayout.y - cutoutPadding;
      const w = targetLayout.width + cutoutPadding * 2;
      const h = targetLayout.height + cutoutPadding * 2;

      const isFirstRender = prevLayoutRef.current === null;
      prevLayoutRef.current = targetLayout;

      if (isFirstRender) {
        cutoutX.value = x;
        cutoutY.value = y;
        cutoutW.value = w;
        cutoutH.value = h;
        overlayOpacity.value = withTiming(1, { duration: FADE_DURATION });
      } else {
        cutoutX.value = withTiming(x, { duration: ANIMATION_DURATION });
        cutoutY.value = withTiming(y, { duration: ANIMATION_DURATION });
        cutoutW.value = withTiming(w, { duration: ANIMATION_DURATION });
        cutoutH.value = withTiming(h, { duration: ANIMATION_DURATION });
      }
    } else {
      overlayOpacity.value = withTiming(0, { duration: FADE_DURATION });
      prevLayoutRef.current = null;
    }
  }, [
    visible,
    targetLayout,
    cutoutPadding,
    overlayOpacity,
    cutoutX,
    cutoutY,
    cutoutW,
    cutoutH,
  ]);

  const cutoutAnimatedProps = useAnimatedProps(() => ({
    x: cutoutX.value,
    y: cutoutY.value,
    width: cutoutW.value,
    height: cutoutH.value,
  }));

  const overlayAnimatedStyle = useAnimatedStyle(() => ({
    opacity: overlayOpacity.value,
  }));

  if (!visible || !targetLayout) {
    return null;
  }

  const placement = computePlacement(
    targetLayout,
    cutoutPadding,
    screenHeight,
    tooltipHeight,
    tooltipPosition,
  );

  const tooltipTop = computeTooltipTop(
    targetLayout,
    cutoutPadding,
    placement,
    tooltipHeight,
  );

  const tooltipLeft = computeTooltipLeft(targetLayout, screenWidth);

  const arrowLeft =
    targetLayout.x + targetLayout.width / 2 - tooltipLeft - 6;
  const clampedArrowLeft = Math.max(16, Math.min(arrowLeft, TOOLTIP_MAX_WIDTH - 28));

  return (
    <Modal
      transparent
      visible={visible}
      statusBarTranslucent
      animationType="none"
    >
      <Animated.View
        testID={testID ?? 'spotlight-overlay'}
        style={[{ flex: 1 }, overlayAnimatedStyle]}
      >
        <Pressable
          testID="spotlight-dismiss"
          style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
          onPress={onDismiss}
          accessibilityLabel="Dismiss spotlight"
          accessibilityRole="button"
        />

        <Svg
          width={screenWidth}
          height={screenHeight}
          style={{ position: 'absolute', top: 0, left: 0 }}
          pointerEvents="none"
        >
          <Defs>
            <Mask id="spotlight-mask">
              <Rect
                x="0"
                y="0"
                width={screenWidth}
                height={screenHeight}
                fill="white"
              />
              <AnimatedRect
                fill="black"
                rx={cutoutBorderRadius}
                ry={cutoutBorderRadius}
                animatedProps={cutoutAnimatedProps}
              />
            </Mask>
          </Defs>
          <Rect
            x="0"
            y="0"
            width={screenWidth}
            height={screenHeight}
            fill="rgba(0,0,0,0.8)"
            mask="url(#spotlight-mask)"
          />

          {/* Outer glow */}
          <AnimatedRect
            fill="none"
            stroke="rgba(185 185 185 / 0.15)"
            strokeWidth={10}
            rx={cutoutBorderRadius}
            ry={cutoutBorderRadius}
            animatedProps={cutoutAnimatedProps}
          />
          {/* Inner glow */}
          <AnimatedRect
            fill="none"
            stroke="rgba(185 185 185 / 0.35)"
            strokeWidth={0}
            rx={cutoutBorderRadius}
            ry={cutoutBorderRadius}
            animatedProps={cutoutAnimatedProps}
          />
        </Svg>

        {children && (
          <View
            testID="spotlight-tooltip-container"
            style={{
              position: 'absolute',
              top: tooltipTop,
              left: tooltipLeft,
              maxWidth: TOOLTIP_MAX_WIDTH,
              width: TOOLTIP_MAX_WIDTH,
            }}
            onLayout={(e) => setTooltipHeight(e.nativeEvent.layout.height)}
          >
            {placement === 'below' && (
              <View
                testID="spotlight-arrow"
                style={{
                  width: 12,
                  height: 12,
                  backgroundColor: '#1E1C19',
                  borderTopWidth: 1,
                  borderLeftWidth: 1,
                  borderColor: '#2A2826',
                  transform: [{ rotate: '45deg' }],
                  position: 'absolute',
                  top: -6,
                  left: clampedArrowLeft,
                  zIndex: 1,
                }}
              />
            )}

            {children}

            {placement === 'above' && (
              <View
                testID="spotlight-arrow"
                style={{
                  width: 12,
                  height: 12,
                  backgroundColor: '#1E1C19',
                  borderBottomWidth: 1,
                  borderRightWidth: 1,
                  borderColor: '#2A2826',
                  transform: [{ rotate: '45deg' }],
                  position: 'absolute',
                  bottom: -6,
                  left: clampedArrowLeft,
                  zIndex: 1,
                }}
              />
            )}
          </View>
        )}
      </Animated.View>
    </Modal>
  );
}

export { Spotlight };
export type { SpotlightProps, TargetLayout, TooltipPlacement };

